import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import { toast } from 'sonner';
import { Crown, Shield, Users, CheckCircle, XCircle, Search, UserCog, Trash2, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [activeFilter, setActiveFilter] = useState(null); // 'premium', 'trial', 'admin', or null
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    checkAdminAndFetchUsers();
  }, []);

  const checkAdminAndFetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const statusRes = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!statusRes.data.is_admin) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }
      
      setIsAdmin(true);
      
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        setIsAdmin(false);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  };

  const grantPremium = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/users/${userId}/grant-premium`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Premium access granted');
      checkAdminAndFetchUsers();
    } catch (error) {
      toast.error('Failed to grant access');
    }
  };

  const revokePremium = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/users/${userId}/revoke-premium`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Premium access revoked');
      checkAdminAndFetchUsers();
    } catch (error) {
      toast.error('Failed to revoke access');
    }
  };

  const makeAdmin = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/users/${userId}/make-admin`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User is now an admin');
      checkAdminAndFetchUsers();
    } catch (error) {
      toast.error('Failed to make admin');
    }
  };

  const removeAdmin = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/users/${userId}/remove-admin`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Admin access removed');
      checkAdminAndFetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove admin');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted');
      checkAdminAndFetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const deleteSelectedUsers = async () => {
    if (selectedUsers.length === 0) {
      toast.error('No users selected');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete ${selectedUsers.length} user(s)? This action cannot be undone.`)) {
      return;
    }
    
    setDeleting(true);
    const token = localStorage.getItem('token');
    let successCount = 0;
    let failCount = 0;
    
    for (const userId of selectedUsers) {
      try {
        await axios.delete(`${API}/admin/users/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        successCount++;
      } catch (error) {
        failCount++;
      }
    }
    
    setDeleting(false);
    setSelectedUsers([]);
    
    if (successCount > 0) {
      toast.success(`Deleted ${successCount} user(s)`);
    }
    if (failCount > 0) {
      toast.error(`Failed to delete ${failCount} user(s)`);
    }
    
    checkAdminAndFetchUsers();
  };

  const toggleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(u => u.id));
    }
  };

  // Calculate stats
  const premiumCount = users.filter(u => u.subscription_plan === 'premium' && u.subscription_status !== 'trial').length;
  const trialCount = users.filter(u => u.subscription_status === 'trial').length;
  const adminCount = users.filter(u => u.is_admin).length;

  // Filter users based on search and active filter
  const filteredUsers = users.filter(u => {
    // Search filter
    const matchesSearch = 
      u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    // Category filter
    if (activeFilter === 'premium') {
      return u.subscription_plan === 'premium' && u.subscription_status !== 'trial';
    } else if (activeFilter === 'trial') {
      return u.subscription_status === 'trial';
    } else if (activeFilter === 'admin') {
      return u.is_admin;
    }
    
    return true;
  });

  const handleFilterClick = (filter) => {
    setActiveFilter(activeFilter === filter ? null : filter);
    setSelectedUsers([]);
  };

  if (loading) {
    return (
      <div className="min-h-screen pb-24 md:pb-8">
        <Navigation user={user} onLogout={onLogout} currentPage="admin" />
        <div className="container mx-auto px-4 py-8">
          <p className="text-center">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen pb-24 md:pb-8">
        <Navigation user={user} onLogout={onLogout} currentPage="admin" />
        <div className="container mx-auto px-4 py-8">
          <div className="card text-center py-12">
            <Shield size={48} className="mx-auto mb-4 text-gray-400" />
            <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-6">You don't have admin access to view this page.</p>
            <button
              onClick={() => navigate('/')}
              className="btn"
              style={{ backgroundColor: '#A2DAD3', color: 'white' }}
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 md:pb-8" data-testid="admin-dashboard">
      <Navigation user={user} onLogout={onLogout} currentPage="admin" />
      
      <div className="container mx-auto px-4 py-6 sm:py-8">
        <div className="flex items-center gap-3 mb-6 sm:mb-8">
          <Shield size={28} className="sm:w-8 sm:h-8" style={{ color: '#9333ea' }} />
          <h1 className="text-2xl sm:text-3xl font-bold">Admin Dashboard</h1>
        </div>

        {/* Stats Cards - Clickable Filters */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <div className="card p-3 sm:p-4 cursor-default">
            <div className="flex items-center gap-2 sm:gap-3">
              <Users size={20} className="sm:w-6 sm:h-6" style={{ color: '#A2DAD3' }} />
              <div>
                <p className="text-xl sm:text-2xl font-bold">{users.length}</p>
                <p className="text-xs sm:text-sm text-gray-600">Total Users</p>
              </div>
            </div>
          </div>
          
          <div 
            className={`card p-3 sm:p-4 cursor-pointer transition-all hover:scale-105 ${activeFilter === 'premium' ? 'ring-2 ring-offset-2' : ''}`}
            style={{ 
              backgroundColor: activeFilter === 'premium' ? '#f0f9f8' : 'white',
              ringColor: '#A2DAD3'
            }}
            onClick={() => handleFilterClick('premium')}
          >
            <div className="flex items-center gap-2 sm:gap-3">
              <Crown size={20} className="sm:w-6 sm:h-6" style={{ color: '#A2DAD3' }} />
              <div>
                <p className="text-xl sm:text-2xl font-bold">{premiumCount}</p>
                <p className="text-xs sm:text-sm text-gray-600">Premium</p>
              </div>
            </div>
          </div>
          
          <div 
            className={`card p-3 sm:p-4 cursor-pointer transition-all hover:scale-105 ${activeFilter === 'trial' ? 'ring-2 ring-offset-2' : ''}`}
            style={{ 
              backgroundColor: activeFilter === 'trial' ? '#fffbeb' : 'white',
              ringColor: '#FFC107'
            }}
            onClick={() => handleFilterClick('trial')}
          >
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: '#FFC107', color: 'white' }}>T</div>
              <div>
                <p className="text-xl sm:text-2xl font-bold">{trialCount}</p>
                <p className="text-xs sm:text-sm text-gray-600">On Trial</p>
              </div>
            </div>
          </div>
          
          <div 
            className={`card p-3 sm:p-4 cursor-pointer transition-all hover:scale-105 ${activeFilter === 'admin' ? 'ring-2 ring-offset-2' : ''}`}
            style={{ 
              backgroundColor: activeFilter === 'admin' ? '#faf5ff' : 'white',
              ringColor: '#9333ea'
            }}
            onClick={() => handleFilterClick('admin')}
          >
            <div className="flex items-center gap-2 sm:gap-3">
              <UserCog size={20} className="sm:w-6 sm:h-6" style={{ color: '#9333ea' }} />
              <div>
                <p className="text-xl sm:text-2xl font-bold">{adminCount}</p>
                <p className="text-xs sm:text-sm text-gray-600">Admins</p>
              </div>
            </div>
          </div>
        </div>

        {/* Active Filter Badge */}
        {activeFilter && (
          <div className="mb-4 flex items-center gap-2">
            <span className="text-sm text-gray-600">Filtering by:</span>
            <span 
              className="px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2"
              style={{ 
                backgroundColor: activeFilter === 'premium' ? '#A2DAD3' : activeFilter === 'trial' ? '#FFC107' : '#9333ea',
                color: 'white'
              }}
            >
              {activeFilter === 'premium' ? 'Premium Users' : activeFilter === 'trial' ? 'Trial Users' : 'Admins'}
              <button onClick={() => setActiveFilter(null)} className="hover:opacity-80">
                <X size={14} />
              </button>
            </span>
          </div>
        )}

        {/* Search and Actions Bar */}
        <div className="card mb-6">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search users by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10"
                data-testid="search-users"
              />
            </div>
            
            {selectedUsers.length > 0 && (
              <button
                onClick={deleteSelectedUsers}
                disabled={deleting}
                className="btn flex items-center gap-2 text-sm px-4 py-2"
                style={{ backgroundColor: '#ef4444', color: 'white' }}
                data-testid="delete-selected"
              >
                <Trash2 size={16} />
                {deleting ? 'Deleting...' : `Delete Selected (${selectedUsers.length})`}
              </button>
            )}
          </div>
        </div>

        {/* Select All Checkbox */}
        {filteredUsers.length > 0 && (
          <div className="mb-4 flex items-center gap-3">
            <input
              type="checkbox"
              id="selectAll"
              checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
              onChange={toggleSelectAll}
              className="w-4 h-4 rounded"
            />
            <label htmlFor="selectAll" className="text-sm text-gray-600 cursor-pointer">
              Select all ({filteredUsers.length} users)
            </label>
          </div>
        )}

        {/* Users List */}
        <div className="space-y-3 sm:space-y-4">
          {filteredUsers.map(u => (
            <div 
              key={u.id} 
              className={`card transition-all ${selectedUsers.includes(u.id) ? 'ring-2 ring-red-300 bg-red-50' : ''}`}
              data-testid={`user-card-${u.id}`}
            >
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <input
                    type="checkbox"
                    checked={selectedUsers.includes(u.id)}
                    onChange={() => toggleSelectUser(u.id)}
                    className="w-4 h-4 rounded mt-1 flex-shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="font-semibold text-base sm:text-lg truncate">{u.name}</h3>
                      {u.is_admin && (
                        <span className="px-2 py-0.5 rounded-full text-xs flex items-center gap-1 bg-purple-100 text-purple-700 flex-shrink-0">
                          <Shield size={10} />
                          Admin
                        </span>
                      )}
                      {u.subscription_plan === 'premium' && u.subscription_status !== 'trial' && (
                        <span className="px-2 py-0.5 rounded-full text-xs flex items-center gap-1 flex-shrink-0"
                              style={{ backgroundColor: '#A2DAD3', color: 'white' }}>
                          <Crown size={10} />
                          Premium
                        </span>
                      )}
                      {u.subscription_status === 'trial' && (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-700 flex-shrink-0">
                          Trial
                        </span>
                      )}
                      {u.admin_granted_premium && (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700 flex-shrink-0">
                          Granted
                        </span>
                      )}
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600 truncate">{u.email}</p>
                    <div className="flex flex-wrap gap-x-2 gap-y-1 mt-1 text-[10px] sm:text-xs text-gray-500">
                      <span>Joined: {new Date(u.created_at).toLocaleDateString()}</span>
                      {u.trial_end_date && (
                        <span>• Trial ends: {new Date(u.trial_end_date).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-2 flex-wrap pl-7 lg:pl-0">
                  {/* Premium Controls */}
                  {u.subscription_plan === 'premium' && u.admin_granted_premium ? (
                    <button
                      onClick={() => revokePremium(u.id)}
                      className="btn text-xs sm:text-sm px-3 py-1.5 flex items-center gap-1"
                      style={{ backgroundColor: '#F9A7A7', color: 'white' }}
                      data-testid={`revoke-premium-${u.id}`}
                    >
                      <XCircle size={14} />
                      <span className="hidden sm:inline">Revoke</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => grantPremium(u.id)}
                      className="btn text-xs sm:text-sm px-3 py-1.5 flex items-center gap-1"
                      style={{ backgroundColor: '#A2DAD3', color: 'white' }}
                      data-testid={`grant-premium-${u.id}`}
                    >
                      <CheckCircle size={14} />
                      <span className="hidden sm:inline">Grant</span>
                    </button>
                  )}
                  
                  {/* Admin Controls */}
                  {u.id !== user?.id && (
                    u.is_admin ? (
                      <button
                        onClick={() => removeAdmin(u.id)}
                        className="btn text-xs sm:text-sm px-3 py-1.5 flex items-center gap-1 bg-gray-200 text-gray-700"
                        data-testid={`remove-admin-${u.id}`}
                      >
                        <UserCog size={14} />
                        <span className="hidden sm:inline">Remove Admin</span>
                      </button>
                    ) : (
                      <button
                        onClick={() => makeAdmin(u.id)}
                        className="btn text-xs sm:text-sm px-3 py-1.5 flex items-center gap-1 bg-purple-100 text-purple-700"
                        data-testid={`make-admin-${u.id}`}
                      >
                        <UserCog size={14} />
                        <span className="hidden sm:inline">Make Admin</span>
                      </button>
                    )
                  )}
                  
                  {/* Delete Button */}
                  {u.id !== user?.id && (
                    <button
                      onClick={() => deleteUser(u.id)}
                      className="btn text-xs sm:text-sm px-3 py-1.5 flex items-center gap-1 bg-red-100 text-red-700 hover:bg-red-200"
                      data-testid={`delete-user-${u.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {filteredUsers.length === 0 && (
            <div className="card text-center py-8">
              <p className="text-gray-500">
                {activeFilter ? `No ${activeFilter} users found` : 'No users found'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
